import { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ClipboardList, FileText, GraduationCap, UserPlus } from 'lucide-react';
import Button from '@/components/Button';
import { newsContent } from '@/data/news';
import ContactForm from '@/components/ContactForm';
import SectionTitle from '@/components/SectionTitle';
import JoinUsSection from '@/components/sections/JoinUsSection';
const VIRTUAL_TOUR_URL = 'https://tours.tourify.cl/tours/L23x4Io2fJLPr';
// Parámetros del visor para el banner: mls=1 oculta el botón de play; disable=... oculta el logo,
// los botones de la derecha (compartir, pantalla completa, VR) y los de contacto/plano.
const VIRTUAL_TOUR_BANNER_URL = `${VIRTUAL_TOUR_URL}?mls=1&disable=logo,controls,leadgen,floorplan,request`;

// import NewYearModal from '@/components/modals/NewYearModal'; // Desactivado temporalmente

export default function Home() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [isNewsModal, setIsNewsModal] = useState(false);
  const [newsStartIndex, setNewsStartIndex] = useState(0);
  const [isNewYearModalOpen, setIsNewYearModalOpen] = useState(false);

  // Modal de Año Nuevo desactivado
  // useEffect(() => {
  //   const hasSeenNewYear = sessionStorage.getItem('hasSeenNewYear');
  //   if (!hasSeenNewYear) {
  //     setIsNewYearModalOpen(true);
  //     sessionStorage.setItem('hasSeenNewYear', 'true');
  //   }
  // }, []);

  const openModal = (type, index = 0) => {
    if (type === "news") {
      setModalContent(newsContent);
      setNewsStartIndex(index);
      setIsNewsModal(true);
    } else {
      setModalContent([{ title: "Información", moreText: "Este es un cuadro de texto simple." }]);
      setIsNewsModal(false);
    }
    setIsModalOpen(true);
  };

  return (
    <main className="flex-1 w-full flex flex-col items-center">
      <BannerSection />
      <QuickAccessSection />
      <NewsSection openModal={openModal} />
      <JoinUsSection />
      
      {/* Modales */}
      {/* Modal de Año Nuevo desactivado */}
      {/* {isNewYearModalOpen && (
        <NewYearModal onClose={() => setIsNewYearModalOpen(false)} />
      )} */}
      {isModalOpen && (
        isNewsModal ? (
          <NewsModal
            news={modalContent}
            initialIndex={newsStartIndex}
            onClose={() => setIsModalOpen(false)}
          />
        ) : (
          <SimpleModal
            content={modalContent[0]}
            onClose={() => setIsModalOpen(false)}
          />
        )
      )}
    </main>
  );
}

// Sección del Banner: el recorrido virtual interactivo, y debajo la franja de bienvenida.
// El texto va fuera del recorrido para que se lea siempre, aunque el fondo se mueva.
const BannerSection = () => {
  // Con "ahorro de datos" activado en el dispositivo no se descargan las ~5 MB del tour sin que el usuario lo pida
  const [shouldLoadTour, setShouldLoadTour] = useState(
    () => typeof navigator === 'undefined' || navigator.connection?.saveData !== true
  );
  const [tourLoaded, setTourLoaded] = useState(false);

  return (
    <div className="w-full">
      <section className="w-full relative overflow-hidden bg-green-900 h-[60svh] md:h-[calc(100svh-7.25rem-9rem)] min-h-[420px] max-h-[820px]">
        {/* Fondo de carga: se ve hasta que el visor del tour termina de cargar */}
        <div className="absolute inset-0 flex items-center justify-center">
          {shouldLoadTour ? (
            <div
              className="h-10 w-10 animate-spin rounded-full border-4 border-white/25 border-t-white"
              role="status"
              aria-label="Cargando recorrido virtual"
            />
          ) : (
            <Button variant="light" onClick={() => setShouldLoadTour(true)}>
              Ver recorrido virtual
            </Button>
          )}
        </div>

        {/* El botón "Sitio Web" del tour es código propio del tour y no se puede ocultar por
            parámetro: el iframe se extiende hacia arriba y el contenedor lo recorta. */}
        {shouldLoadTour && (
          <iframe
            src={VIRTUAL_TOUR_BANNER_URL}
            title="Recorrido virtual del Colegio Nuevo Milenio"
            allow="accelerometer;gyroscope;fullscreen"
            allowFullScreen
            onLoad={() => setTourLoaded(true)}
            className={`absolute left-0 w-full border-0 transition-opacity duration-500 ${tourLoaded ? 'opacity-100' : 'opacity-0'}`}
            style={{ top: "-110px", height: "calc(100% + 110px)" }}
          />
        )}
      </section>

      <div className="h-1.5 w-full bg-accent" aria-hidden="true" />

      <section className="w-full bg-green-900 text-white">
        <div className="container mx-auto px-6 md:px-8 py-6 md:py-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5 md:gap-8 text-center md:text-left">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              Bienvenidos a Colegio Nuevo Milenio
            </h1>
            <p className="mt-2 text-base md:text-lg text-beige-100">
              Formando líderes para el futuro con educación de calidad y valores sólidos.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 shrink-0">
            <Button href="/quienes-somos" variant="light">
              Quiénes Somos
            </Button>
            <ContactForm variant="outline" />
          </div>
        </div>
      </section>
    </div>
  );
};

const formatNewsDate = (isoDate) => {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('es-CL', { day: 'numeric', month: 'long', year: 'numeric' });
};

// Quita el saludo inicial ("Estimadas familias...,") si existe y usa el primer párrafo como extracto
const newsExcerpt = (content) => {
  const lines = content.split('\n').map((line) => line.trim()).filter(Boolean);
  const hasGreeting = lines.length > 1 && lines[0].endsWith(',');
  return (hasGreeting ? lines[1] : lines[0]) || '';
};

const QUICK_ACCESS = [
  { to: "/becas", title: "Becas 2027", description: "Información y resultados de becas", icon: GraduationCap },
  { to: "/admision", title: "Admisión 2027", description: "Calendario y proceso de matrícula para alumnos nuevos", icon: UserPlus },
  { to: "/lista-de-utiles", title: "Lista de útiles", description: "Listas por nivel, de Pre Kinder a 4° Medio", icon: ClipboardList },
  { to: "/documentos", title: "Documentos", description: "Reglamentos, protocolos y documentos oficiales", icon: FileText },
];

// Banda de color sólido con cajas más oscuras e íconos de línea (como "Según los números" en nido.cl)
const QuickAccessSection = () => (
  <section className="w-full bg-green-900 py-14 md:py-16" aria-labelledby="accesos-titulo">
    <div className="container mx-auto px-6 md:px-8">
      <SectionTitle tone="light"><span id="accesos-titulo">Accesos rápidos</span></SectionTitle>
      <ul className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {QUICK_ACCESS.map((item) => {
          const Icon = item.icon;
          const className = "group flex h-full flex-col items-center border-b-4 border-accent bg-black/25 px-6 py-10 text-center text-white transition-colors hover:bg-black/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white";
          const content = (
            <>
              <Icon className="h-14 w-14 text-beige-100/70" strokeWidth={1.25} aria-hidden="true" />
              <span className="mt-6 text-lg font-semibold uppercase tracking-wide">{item.title}</span>
              <span className="mt-3 text-sm leading-relaxed text-white/80">{item.description}</span>
            </>
          );
          return (
            <li key={item.title}>
              {item.to ? (
                <Link to={item.to} className={className}>{content}</Link>
              ) : (
                <a href={item.href} target="_blank" rel="noopener noreferrer" className={className}>{content}</a>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  </section>
);

// Carrusel de noticias: desplazamiento con scroll-snap (táctil/trackpad) y flechas, 1/2/3 tarjetas según el ancho
const NewsSection = ({ openModal }) => {
  const trackRef = useRef(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);

  const updateScrollState = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    setCanScrollPrev(track.scrollLeft > 4);
    setCanScrollNext(track.scrollLeft + track.clientWidth < track.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateScrollState();
    window.addEventListener('resize', updateScrollState);
    return () => window.removeEventListener('resize', updateScrollState);
  }, [updateScrollState]);

  // Avanza una tarjeta (ancho de la primera + el gap de 1.5rem)
  const scrollByCard = (direction) => {
    const track = trackRef.current;
    const card = track?.firstElementChild;
    if (!card) return;
    track.scrollBy({ left: direction * (card.offsetWidth + 24), behavior: 'smooth' });
  };

  const arrowClassName = "absolute top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center border border-gray-300 bg-white text-green-900 shadow-md transition-colors hover:border-green-900 hover:bg-green-900 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-900 disabled:pointer-events-none disabled:opacity-0 sm:flex";

  return (
    <section className="w-full bg-white py-14 md:py-20" aria-labelledby="noticias-titulo">
      <div className="container mx-auto px-6 md:px-8">
        <SectionTitle><span id="noticias-titulo">Noticias</span></SectionTitle>
        <div className="relative mt-10" role="region" aria-roledescription="carrusel" aria-label="Noticias">
          <button
            type="button"
            onClick={() => scrollByCard(-1)}
            disabled={!canScrollPrev}
            aria-label="Noticias anteriores"
            className={`${arrowClassName} -left-5 lg:-left-6`}
          >
            <ChevronLeft className="h-6 w-6" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => scrollByCard(1)}
            disabled={!canScrollNext}
            aria-label="Noticias siguientes"
            className={`${arrowClassName} -right-5 lg:-right-6`}
          >
            <ChevronRight className="h-6 w-6" aria-hidden="true" />
          </button>
          <ul
            ref={trackRef}
            onScroll={updateScrollState}
            className="flex snap-x snap-mandatory gap-6 overflow-x-auto scroll-smooth pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {newsContent.map((item, index) => (
              <li
                key={item.id}
                className="w-full shrink-0 snap-start md:w-[calc((100%-1.5rem)/2)] lg:w-[calc((100%-3rem)/3)]"
              >
                <button
                  type="button"
                  onClick={() => openModal("news", index)}
                  className="flex h-full w-full flex-col border border-gray-300 text-left transition-colors hover:border-green-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-900"
                >
                  {item.image && (
                    <img src={item.image} alt="" loading="lazy" className="h-48 w-full object-cover object-top" />
                  )}
                  <span className="flex flex-1 flex-col p-6">
                    <time dateTime={item.date} className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                      {formatNewsDate(item.date)}
                    </time>
                    <span className="mt-3 line-clamp-3 text-lg font-bold leading-snug text-green-900">{item.title}</span>
                    <span className="mt-3 line-clamp-4 text-sm leading-relaxed text-gray-600">{newsExcerpt(item.content)}</span>
                    <span className="mt-auto pt-5 text-xs font-bold uppercase tracking-widest text-green-900 underline decoration-accent decoration-2 underline-offset-4">
                      Leer más
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

NewsSection.propTypes = {
  openModal: PropTypes.func.isRequired,
};

function NewsModal({ news, initialIndex = 0, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // Manejo de teclado para navegación
  const handleNext = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % news.length);
  }, [news.length]);

  const handlePrev = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + news.length) % news.length);
  }, [news.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        handlePrev();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext, handlePrev, onClose]); // Dependencia del tamaño del array

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header con título y botón cerrar */}
        <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b border-gray-100 flex justify-between items-start">
          <div className="pr-8">
            <h2 className="text-2xl md:text-3xl font-bold text-green-900 leading-tight">
              {news[currentIndex].title}
              {news[currentIndex].isNew && (
                <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 align-middle">
                  Nueva
                </span>
              )}
            </h2>
            <p className="text-sm text-gray-500 mt-1">{formatNewsDate(news[currentIndex].date)}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Cerrar modal"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Contenido */}
        <div className="p-6">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Imagen */}
            {news[currentIndex].image && (
              <div className="lg:w-1/2 flex justify-center bg-gray-50 rounded-lg p-2">
                <img
                  src={news[currentIndex].image}
                  alt={news[currentIndex].title}
                  className="max-h-[500px] w-auto object-contain rounded shadow-sm hover:scale-[1.02] transition-transform duration-300"
                />
              </div>
            )}
            
            {/* Texto */}
            <div className={`flex-1 ${!news[currentIndex].image ? 'w-full' : ''}`}>
              <div className="prose prose-green max-w-none">
                <p className="text-gray-700 whitespace-pre-wrap text-lg leading-relaxed">
                  {news[currentIndex].content}
                </p>
                {news[currentIndex].content2 && (
                   <p className="text-gray-700 whitespace-pre-wrap text-lg leading-relaxed mt-4">
                     {news[currentIndex].content2}
                   </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer con navegación */}
        <div className="mt-auto bg-gray-50 px-6 py-4 border-t border-gray-100 flex justify-between items-center sticky bottom-0">
          <button
            onClick={handlePrev}
            className="flex items-center px-4 py-2 text-sm font-medium text-green-700 bg-white border border-green-200 rounded-lg hover:bg-green-50 hover:text-green-800 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 shadow-sm"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Anterior
          </button>
          
          <span className="text-sm text-gray-500 font-medium hidden sm:block">
            {currentIndex + 1} de {news.length}
          </span>

          <button
            onClick={handleNext}
            className="flex items-center px-4 py-2 text-sm font-medium text-green-700 bg-white border border-green-200 rounded-lg hover:bg-green-50 hover:text-green-800 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 shadow-sm"
          >
            Siguiente
            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

NewsModal.propTypes = {
  initialIndex: PropTypes.number,
  news: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      isNew: PropTypes.bool,
      date: PropTypes.string,
      image: PropTypes.string,
      content: PropTypes.string.isRequired,
      content2: PropTypes.string,
    })
  ).isRequired,
  onClose: PropTypes.func.isRequired,
};

function SimpleModal({ content, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="bg-white p-6 rounded-lg shadow-lg max-w-lg w-full m-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-2xl font-bold mb-4 text-green-800">{content.title}</h2>
        <p className="text-green-700">{content.moreText}</p>
        <Button
          variant="default"
          className="w-full mt-4"
          onClick={onClose}
        >
          Cerrar
        </Button>
      </div>
    </div>
  );
}

SimpleModal.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string.isRequired,
    moreText: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};
